import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import WebPageLayout from '../../../components/layout/WebPageLayout';
import { useNavigate } from 'react-router-dom';
import mainLogo from '../../../assets/Logo/main_logo.png';
import kakaoIcon from '../../../assets/Icon/footer/kakao_icon.png';
import adBannerWeb from '../../../assets/Icon/project/ad_banner_web.png';
import engBannerWeb from '../../../assets/Icon/project/Eng_Web.png';
import loudspeakerIcon from '../../../assets/Icon/project/loudspeaker_icon.png';
import donateIcon from '../../../assets/Icon/project/donate_icon.png';
import { useLogin } from '../useLogin';
import { PATHS } from '../../../routes/paths';
import backgroundImage from '../../../assets/img/background.png';
import {
  MISSIONFUND_PRIVACY_POLICY_URL,
  MISSIONFUND_TERMS_OF_SERVICE_URL,
} from '../../../Utils/missionFundLegalUrls';

// 모금자/후원자 선택은 추후 다시 활성화 예정입니다.
const SHOW_LOGIN_TYPE_TABS: boolean = false;

const LoginWeb = () => {
  const { i18n, t } = useTranslation('translation');
  const navigate = useNavigate();
  const currentLang = (i18n.resolvedLanguage || i18n.language || 'ko').split('-')[0];
  const {
    loginType,
    setLoginType,
    email,
    setEmail,
    password,
    setPassword,
    handleLogin,
    handleKakaoDonorLogin,
    isBusy,
    postSignupHint,
    error,
  } = useLogin();

  const [emailFocused, setEmailFocused] = React.useState(false);
  const [passwordFocused, setPasswordFocused] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const emailInputRef = React.useRef<HTMLInputElement>(null);
  const passwordInputRef = React.useRef<HTMLInputElement>(null);
  const bannerImage = currentLang === 'en' ? engBannerWeb : adBannerWeb;
  const visibleLoginType = SHOW_LOGIN_TYPE_TABS ? loginType : 'fundraiser';

  return (
    <WebPageLayout title="" hideHeader hideFooter>
      <BackgroundOverlay />
      <Centered>
        <LoginContainer>
          <HeaderRow>
            <LangSelect
              value={currentLang}
              onChange={(e) => i18n.changeLanguage(e.target.value)}
            >
              <option value="ko">{t('lang.ko')}</option>
              <option value="en">{t('lang.en')}</option>
            </LangSelect>
          </HeaderRow>
          <LogoSection>
            <LogoLink href="https://missionfund.org/" target="_blank" rel="noopener noreferrer" aria-label="MissionFund homepage">
              <Logo src={mainLogo} alt="Mission Fund" />
            </LogoLink>
          </LogoSection>

          {/* Temporarily hidden: fundraiser/donor selector will be restored later. */}
          {SHOW_LOGIN_TYPE_TABS ? (
            <TabWrapper>
              <Tab
                $active={loginType === 'fundraiser'}
                onClick={() => setLoginType('fundraiser')}
              >
                <TabIcon $active={loginType === 'fundraiser'} $icon={loudspeakerIcon} /> {t('page.login.tabs.fundraiser')}
              </Tab>
              <Tab
                $active={loginType === 'donor'}
                onClick={() => setLoginType('donor')}
              >
                <TabIcon $active={loginType === 'donor'} $icon={donateIcon} /> {t('page.login.tabs.donor')}
              </Tab>
            </TabWrapper>
          ) : null}

          <FormSection $loginType={visibleLoginType}>
            {visibleLoginType === 'fundraiser' ? (
              <>
                <Form onSubmit={handleLogin}>
                  {postSignupHint && (
                    <HintMessage>{t('page.login.messages.signupCompleteSignIn')}</HintMessage>
                  )}
                  <InputWrapper>
                    <InputFieldWrapper 
                      $position="top" 
                      $focused={emailFocused}
                      onClick={() => emailInputRef.current?.focus()}
                    >
                      <InputContent>
                        <FloatingLabel $active={emailFocused || !!email}>
                          {t('page.login.labels.email')}
                        </FloatingLabel>
                        <StyledInput
                          ref={emailInputRef}
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          onFocus={() => setEmailFocused(true)}
                          onBlur={() => setEmailFocused(false)}
                          $hasValue={emailFocused || !!email}
                          required
                        />
                      </InputContent>
                      {email && (
                        <ClearButton type="button" onClick={(e) => { e.stopPropagation(); setEmail(''); }}>
                          <ClearIcon />
                        </ClearButton>
                      )}
                    </InputFieldWrapper>
                    <InputFieldWrapper 
                      $position="bottom" 
                      $focused={passwordFocused}
                      onClick={() => passwordInputRef.current?.focus()}
                    >
                      <InputContent>
                        <FloatingLabel $active={passwordFocused || !!password}>
                          {t('page.login.labels.password')}
                        </FloatingLabel>
                        <StyledInput
                          ref={passwordInputRef}
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          onFocus={() => setPasswordFocused(true)}
                          onBlur={() => setPasswordFocused(false)}
                          $hasValue={passwordFocused || !!password}
                          required
                        />
                      </InputContent>
                      <InputActions>
                        {password && (
                          <>
                            <TogglePasswordButton type="button" onClick={(e) => { e.stopPropagation(); setShowPassword(!showPassword); }}>
                              {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                            </TogglePasswordButton>
                            <ClearButton type="button" onClick={(e) => { e.stopPropagation(); setPassword(''); }}>
                              <ClearIcon />
                            </ClearButton>
                          </>
                        )}
                      </InputActions>
                    </InputFieldWrapper>
                  </InputWrapper>
                  {error && <ErrorMessage>{error}</ErrorMessage>}
                  <SubmitButton type="submit" disabled={isBusy || !email || !password}>
                    {isBusy ? t('page.login.actions.loggingIn') : t('page.login.actions.login')}
                  </SubmitButton>
                </Form>
              </>
            ) : (
              <DonorSection>
                <DonorText>{t('page.login.donor.ctaWeb')}</DonorText>
                <KakaoButton type="button" onClick={handleKakaoDonorLogin}>
                  <KakaoIcon src={kakaoIcon} alt="Kakao" /> {t('page.login.donor.kakaoStart')}
                </KakaoButton>
              </DonorSection>
            )}
          </FormSection>

          {visibleLoginType === 'donor' && (
            <DonorHistoryText type="button" onClick={() => navigate(PATHS.DONATION_HISTORY_FIND)}>
              {t('page.login.donor.historyLink')}
            </DonorHistoryText>
          )}

          {visibleLoginType === 'fundraiser' && (
            <LinkSection>
              <LinkText onClick={() => navigate(PATHS.PASSWORD_FIND)}>{t('page.login.links.findPassword')}</LinkText>
              <VerticalDivider />
              <LinkText $highlight onClick={() => navigate(PATHS.SIGNUP)}>{t('page.login.links.signup')}</LinkText>
            </LinkSection>
          )}

          <BannerSection href="https://missionfund.org/support/" target="_blank" rel="noopener noreferrer">
            <BannerImage src={bannerImage} alt={t('page.login.bannerAlt')} />
          </BannerSection>

          <FooterLinks>
            <FooterLegalLink
              href={MISSIONFUND_TERMS_OF_SERVICE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('common.footer.servicePolicy')}
            </FooterLegalLink>
            <FooterLinkDivider>|</FooterLinkDivider>
            <FooterLegalLink
              href={MISSIONFUND_PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('common.footer.privacyPolicy')}
            </FooterLegalLink>
          </FooterLinks>
        </LoginContainer>
      </Centered>
    </WebPageLayout>
  );
};

const BackgroundOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 0;
  pointer-events: none;
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 60px 0;
  width: 100%;
  position: relative;
  z-index: 1;
`;

const LoginContainer = styled.div`
  width: 560px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const HeaderRow = styled.div`
  position: absolute;
  top: -55px;
  right: -179px;
  z-index: 10;
`;

const LangSelect = styled.select`
  width: auto;
  min-width: 118px;
  height: 42px;
  padding: 8px 36px 8px 12px;
  border-radius: 4px;
  border: 1px solid #BCBCBC;
  background: white;
  color: #676767;
  font-family: 'Pretendard', sans-serif;
  font-size: 20px;
  font-weight: 500;
  cursor: pointer;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='14' height='8' viewBox='0 0 14 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L7 7L13 1' stroke='%23BCBCBC' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 14px 8px;
`;

const LogoSection = styled.div`
  width: 272px;
  height: 85px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  margin-bottom: 40px;
`;

const Logo = styled.img`
  width: 252px;
  height: 65px;
  object-fit: contain;
`;

const LogoLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const TabWrapper = styled.div`
  display: flex;
  width: 100%;
  background: #EEEEEE;
  border-radius: 50px;
  padding: 4px;
  margin-bottom: 12px;
  align-items: center;
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  height: 68px;
  border: ${({ $active }) => ($active ? '2px solid #2BC2E1' : 'none')};
  border-radius: 50px;
  background: ${({ $active }) => ($active ? '#FFFFFF' : 'transparent')};
  color: ${({ $active }) => ($active ? '#17171B' : '#676767')};
  font-family: 'Pretendard', sans-serif;
  font-size: 24px;
  font-weight: 500;
  line-height: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.2s ease-in-out;
`;

const TabIcon = styled.div<{ $active: boolean; $icon: string }>`
  width: 24px;
  height: 24px;
  background-color: ${({ $active }) => ($active ? '#2BC2E1' : '#676767')};
  mask: ${({ $icon }) => `url(${$icon}) no-repeat center / contain`};
  -webkit-mask: ${({ $icon }) => `url(${$icon}) no-repeat center / contain`};
`;

const FormSection = styled.div<{ $loginType: 'fundraiser' | 'donor' }>`
  width: 560px;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[10]};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 0;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: column;
  gap: 0;
  opacity: 1;
  min-height: ${({ $loginType }) => ($loginType === 'fundraiser' ? 'auto' : '274px')};
`;

const DonorSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex: 1;
`;


const DonorText = styled.div`
  width: 100%;
  height: 148px;
  ${({ theme }) => theme.typography.contents.medium24}
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  opacity: 1;
`;

const KakaoButton = styled.button`
  width: 100%;
  height: 62px;
  padding: 10px 24px;
  background: #fee500;
  color: #191919;
  border: none;
  border-radius: 12px;
   ${({ theme }) => theme.typography.contents.medium24}
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
`;

const DonorHistoryText = styled.button`
  margin: 12px 0 92px;
  color: #17171B;
  font-family: Pretendard, sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: auto;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
`;

const KakaoIcon = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const InputFieldWrapper = styled.div<{ $position: 'top' | 'bottom'; $focused: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #FFFFFF;
  min-height: 74px;
  cursor: text;
  transition: border-color 0.2s ease-in-out;
  position: relative;
  z-index: 1;
  
  ${({ $position }) => $position === 'top' ? `
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    border-left: 1px solid #EEEEEE;
    border-top: 1px solid #EEEEEE;
    border-right: 1px solid #EEEEEE;
  ` : `
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
    border: 1px solid #EEEEEE;
  `}
  
  ${({ $focused }) => $focused && `
    border-color: #2BC2E1 !important;
    z-index: 2;
  `}

  ${({ $focused, $position }) => $focused && $position === 'top' && `
    &::after {
      content: '';
      position: absolute;
      left: -1px;
      right: -1px;
      bottom: -1px;
      height: 1px;
      background: #2BC2E1;
      pointer-events: none;
    }
  `}
`;

const InputContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  position: relative;
  justify-content: center;
  height: 100%;
`;

const FloatingLabel = styled.label<{ $active: boolean }>`
  font-family: 'Pretendard', sans-serif;
  transition: all 0.2s ease-in-out;
  pointer-events: none;
  
  ${({ $active }) => $active ? `
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    color: #676767;
    margin-bottom: 2px;
  ` : `
    font-size: 24px;
    font-weight: 400;
    line-height: 36px;
    color: #929292;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  `}
`;

const StyledInput = styled.input<{ $hasValue: boolean }>`
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-family: 'Pretendard', sans-serif;
  font-size: 24px;
  font-weight: 500;
  line-height: 36px;
  color: #17171B;
  padding: 0;
  
  &::placeholder {
    color: transparent;
  }
`;

const InputActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 8px;
`;

const ClearButton = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin-left: 8px;
`;

const ClearIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" fill="#BCBCBC"/>
    <path d="M8 8L16 16M16 8L8 16" stroke="white" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const TogglePasswordButton = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const EyeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 5C7 5 2.73 8.11 1 12.5C2.73 16.89 7 20 12 20C17 20 21.27 16.89 23 12.5C21.27 8.11 17 5 12 5ZM12 17.5C9.24 17.5 7 15.26 7 12.5C7 9.74 9.24 7.5 12 7.5C14.76 7.5 17 9.74 17 12.5C17 15.26 14.76 17.5 12 17.5ZM12 9.5C10.34 9.5 9 10.84 9 12.5C9 14.16 10.34 15.5 12 15.5C13.66 15.5 15 14.16 15 12.5C15 10.84 13.66 9.5 12 9.5Z" fill="#BCBCBC"/>
  </svg>
);

const EyeOffIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 6.5C15.79 6.5 19.17 8.63 20.82 12C20.23 13.31 19.4 14.41 18.41 15.27L19.82 16.68C21.21 15.45 22.31 13.85 23 12C21.27 7.61 17 4.5 12 4.5C10.73 4.5 9.51 4.7 8.36 5.07L10.01 6.72C10.66 6.58 11.32 6.5 12 6.5ZM2.71 3.16L4.69 5.14C2.97 6.42 1.61 8.12 1 12C2.73 16.39 7 19.5 12 19.5C13.52 19.5 14.98 19.21 16.32 18.67L19.03 21.38L20.44 19.97L4.12 3.75L2.71 3.16ZM12 17.5C8.21 17.5 4.83 15.37 3.18 12C3.88 10.49 4.9 9.21 6.13 8.27L8.24 10.38C8.09 10.9 8 11.44 8 12C8 14.21 9.79 16 12 16C12.56 16 13.1 15.91 13.62 15.76L15.26 17.4C14.22 17.78 13.13 18 12 17.5ZM11.86 9.51L14.49 12.14C14.46 10.65 13.35 9.54 11.86 9.51Z" fill="#BCBCBC"/>
  </svg>
);

const HintMessage = styled.div`
  color: #026478;
  font-size: 16px;
  margin-bottom: 12px;
  text-align: center;
  font-weight: 500;
  line-height: 1.5;
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.other.red10};
  font-size: 16px;
  margin-top: 12px;
  text-align: center;
  font-weight: 500;
`;

const SubmitButton = styled.button`
  width: 100%;
  height: 62px;
  padding: 10px 24px;
  background: ${({ disabled, theme }) => (disabled ? '#BCBCBC' : '#026478')};
  color: ${({ disabled }) => (disabled ? '#EEEEEE' : '#FFFFFF')};
  border: none;
  border-radius: 12px;
  font-size: 24px;
  font-weight: 500;
  line-height: 30px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  margin-top: 20px;
  transition: all 0.2s ease-in-out;
  
  &:hover:not(:disabled) {
    background: #014d5c;
  }
`;

const LinkSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 12px;
  margin-bottom: 104px;
`;

const LinkText = styled.button<{ $highlight?: boolean }>`
  background: none;
  border: none;
  padding: 0;
  color: ${({ $highlight, theme }) => ($highlight ? '#2BC2E1' : theme.colors.grayScale[80])};
  font-family: 'Pretendard', sans-serif;
  font-size: 24px;
  font-weight: 500;
  line-height: 100%;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const VerticalDivider = styled.div`
  width: 1px;
  height: 24px;
  background: ${({ theme }) => theme.colors.grayScale[30]};
`;

const BannerSection = styled.a`
  width: 120%;
  cursor: pointer;
  transition: transform 0.2s;
`;

const BannerImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const FooterLinks = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  width: 100%;
`;

const FooterLegalLink = styled.a`
  font-family: 'Pretendard', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #676767;
  cursor: pointer;
  text-decoration: none;
  &:hover {
    color: #17171b;
    text-decoration: underline;
  }
`;

const FooterLinkDivider = styled.span`
  font-family: 'Pretendard', sans-serif;
  font-size: 24px;
  font-weight: 300;
  color: #BCBCBC;
`;

export default LoginWeb;
